import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient } from '@/services/api'
import { dealsApi } from '@/lib/api'
import { useNotificationStore } from '@/stores/notifications'

export interface Deal {
  _id?: string
  id?: string
  name: string
  description?: string
  pipelineDescription?: string
  company?: string
  companyId?: string | {
    _id: string
    name: string
    domain?: string
    industry?: string
  }
  contactId?: string
  contactName?: string
  contactEmail?: string
  contactPhone?: string
  value: number
  currency: string
  status: 'new' | 'contacted' | 'qualified' | 'converted' | 'lost'
  priority?: 'low' | 'normal' | 'medium' | 'high' | 'urgent'
  source: string
  assignedTo?: string | {
    _id: string
    name: string
    email: string
  }
  notes?: string
  tags?: string[]
  pipelineId?: string
  stageId?: string
  rank?: string // LexoRank for ordering within stage
  stageHistory?: { stageId: string; movedAt: string; fromStageId?: string }[]
  probability?: number
  expectedCloseDate?: Date
  createdAt?: Date
  updatedAt?: Date
  lastActivityAt?: Date
  // Board archive: the deal is intact and searchable but hidden from the Kanban board,
  // the deals list and Deal Cadence. Nothing to do with `ArchivedDeal` below, which is a
  // DELETED deal living in the separate `dealarchives` graveyard collection.
  isArchived?: boolean
  archivedAt?: string
  archivedBy?: string
  archiveReason?: string
  // AI recommendation from analysed calls with the deal's contact (call analysis).
  leadQualification?: DealLeadQualification
  // Deals list rows, board cards, the deal detail (nextStep) and the deals-for-contact rows
  // (GET /deals/contact/:id). `null` = looked, there is none; absent = the API did not say (older
  // API, or that lookup failed), so render the same blank for both.
  nextStep?: DealNextStep | null
  plan?: DealPlan | null
  // Deal detail (GET /deals/:id) and the deals-for-contact rows (GET /deals/contact/:id): when the
  // deal entered its current stage (ISO), null when its stage history has no entry for it; absent
  // from an older API.
  stageEnteredAt?: string | null
  // Deal detail (GET /deals/:id) only: the deal has a contactId but that contact is gone (deleted
  // or soft-deleted), so the page asks nothing about it. Absent from an older API.
  contactMissing?: boolean
  // Deal detail only: the deals merged into this one (POST /deals/merge), and, when the page asked
  // for the id of a deal that was merged away, that id (the answer is its survivor).
  mergedFrom?: { dealId: string; name: string; mergedAt: string; mergedBy?: string }[]
  redirectedFrom?: string
}

/** The earliest-due open (not completed) activity linked to a deal. */
export interface DealNextStep {
  _id: string
  title: string
  /** Activity type: 'task' | 'call' | 'meeting'. */
  type: string
  /** ISO timestamp. */
  dueAt: string | null
}

/** The plan a deal sells: its most recent recurring line item's product, and pricing tier when it has one. */
export interface DealPlan {
  product: string
  tier?: string
}

/** Trimmed lead qualification of the deal's contact, as the deals list and board receive it. */
export interface DealLeadQualification {
  status: 'in_progress' | 'qualified' | 'nurture' | 'disqualified'
  score?: number
  reason?: string
  overridden?: boolean
  conversations?: number
  lastAnalyzedAt?: string
  /** 'history': scored from notes and call history (AI review), not from calls. */
  source?: 'calls' | 'history'
  historyAt?: string
  /** The context-aware assessment behind the score, when there is one: the basis line under the pill. */
  assessedAt?: string
  basedOnNotes?: number
  basedOnActivities?: number
}

// A DELETED deal, copied into the `dealarchives` collection by DELETE /deals/:id. Shown in
// the UI as "Deleted Deals". Distinct from an archived (hidden-from-board) deal above.
export interface ArchivedDeal {
  _id: string
  originalId: string
  name: string
  description?: string
  company?: string
  companyId?: string
  contactName?: string
  contactEmail?: string
  value: number
  currency: string
  status: string
  source?: string
  assignedTo?: { _id: string; name: string }
  archivedAt: string
  archivedBy: string
  archivedByUser?: { _id: string; name: string }
  archiveReason?: string
}

export interface ArchivedDealsMeta {
  page: number
  pageSize: number
  total: number
  hasNext: boolean
}

export interface DealsMeta {
  page: number
  pageSize: number
  total: number
  hasNext: boolean
}

/** Read-only extras the reads add (GET /deals/:id, list rows, board cards) and a PATCH answer lacks. */
const READ_ONLY_EXTRAS = ['nextStep', 'stageEnteredAt', 'leadQualification', 'plan', 'contactMissing'] as const

/** The id of a reference that may be populated (`{ _id, name }`) or bare. */
function refId(ref: unknown): string | null {
  if (typeof ref === 'string') return ref
  if (ref && typeof ref === 'object' && typeof (ref as { _id?: unknown })._id === 'string') return (ref as { _id: string })._id
  return null
}

/**
 * `next` (a PATCH answer) with any read-only extra it does not carry taken from `previous`. A
 * field the answer does carry, even as null, wins. The answer is also depopulated (the update
 * stores bare ids): an owner or company it names by the same id as the populated one on screen
 * keeps that populated object, so the page does not flip to "Deleted user" or lose the company's
 * details. `contactMissing` is about the contact it was read with: dropped when the answer links
 * another contact.
 */
function keepReadOnlyExtras(previous: Deal | null | undefined, next: Deal): Deal {
  if (!previous) return next
  const merged: Deal = { ...next }
  for (const key of READ_ONLY_EXTRAS) {
    if (!(key in next) && key in previous) Object.assign(merged, { [key]: previous[key] })
  }
  for (const key of ['assignedTo', 'companyId'] as const) {
    const before = previous[key]
    if (typeof next[key] === 'string' && before && typeof before === 'object' && refId(before) === next[key]) {
      Object.assign(merged, { [key]: before })
    }
  }
  if ('contactId' in next && refId(next.contactId) !== refId(previous.contactId)) delete merged.contactMissing
  return merged
}

export const useDealsStore = defineStore('deals', () => {
  // State
  const deals = ref<Deal[]>([])
  const currentDeal = ref<Deal | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const meta = ref<DealsMeta | null>(null)

  // Archive state
  const archivedDeals = ref<ArchivedDeal[]>([])
  const archivedMeta = ref<ArchivedDealsMeta | null>(null)

  // Computed
  const totalDeals = computed(() => deals.value.length)
  const newDeals = computed(() => deals.value.filter(l => l.status === 'new').length)
  const qualifiedDeals = computed(() => deals.value.filter(l => l.status === 'qualified').length)
  const totalValue = computed(() =>
    deals.value.reduce((sum, deal) => sum + deal.value, 0)
  )

  // Deals needing attention (no activity in X days)
  const staleDeals = computed(() => {
    const now = new Date()
    return deals.value
      .filter(deal => deal.status !== 'converted' && deal.status !== 'lost')
      .map(deal => {
        const updatedAt = deal.updatedAt ? new Date(deal.updatedAt) : now
        const daysSinceActivity = Math.floor((now.getTime() - updatedAt.getTime()) / (1000 * 60 * 60 * 24))
        return { ...deal, daysSinceActivity }
      })
      .filter(deal => deal.daysSinceActivity >= 7)
      .sort((a, b) => b.daysSinceActivity - a.daysSinceActivity)
  })

  // Deals closing this week
  const dealsClosingThisWeek = computed(() => {
    const now = new Date()
    const weekEnd = new Date(now)
    weekEnd.setDate(weekEnd.getDate() + 7)

    return deals.value.filter(deal => {
      if (!deal.expectedCloseDate || deal.status === 'converted' || deal.status === 'lost') return false
      const closeDate = new Date(deal.expectedCloseDate)
      return closeDate >= now && closeDate <= weekEnd
    })
  })

  // Open deals (active pipeline)
  const openDeals = computed(() =>
    deals.value.filter(deal => deal.status !== 'converted' && deal.status !== 'lost')
  )

  // Actions
  // Latest request wins: rapid back/forward or an org switch can leave an older, slower list
  // response arriving last, which must not overwrite the newer list. A replace bumps the
  // sequence; an append (load more) belongs to the current list and is dropped if one replaced it.
  let listSeq = 0
  const fetchDeals = async (params?: any, append: boolean = false) => {
    const seq = append ? listSeq : ++listSeq
    loading.value = true
    error.value = null
    try {
      const response = await dealsApi.list(params)
      if (seq !== listSeq) return deals.value
      const rawDeals = response.data || []
      // Map _id to id for frontend compatibility
      const mappedDeals = rawDeals.map((deal: any) => ({
        ...deal,
        id: deal._id,
        createdAt: deal.createdAt ? new Date(deal.createdAt) : undefined,
        updatedAt: deal.updatedAt ? new Date(deal.updatedAt) : undefined,
      }))

      if (append) {
        // Append new deals to existing list (for infinite scroll)
        deals.value = [...deals.value, ...mappedDeals]
      } else {
        // Replace deals list
        deals.value = mappedDeals
      }
      // Store pagination metadata
      meta.value = response.meta || null
      return deals.value
    } catch (err: any) {
      if (seq !== listSeq) return deals.value
      error.value = err.message || 'Failed to fetch deals'
      throw err
    } finally {
      if (seq === listSeq) loading.value = false
    }
  }

  // Latest request wins here too: a slower response for the deal opened before (a quick view
  // closed and reopened on another card, fast back/forward) must not replace the newer one in
  // currentDeal. Every caller still gets the deal it asked for as the return value.
  let dealSeq = 0
  const fetchDeal = async (id: string) => {
    const seq = ++dealSeq
    loading.value = true
    error.value = null
    try {
      const response = await apiClient.get(`/deals/${id}`)
      const rawDeal = response.data.data
      const fetched: Deal = {
        ...rawDeal,
        id: rawDeal._id,
        createdAt: rawDeal.createdAt ? new Date(rawDeal.createdAt) : undefined,
        updatedAt: rawDeal.updatedAt ? new Date(rawDeal.updatedAt) : undefined,
      }
      if (seq === dealSeq) currentDeal.value = fetched
      return fetched
    } catch (err: any) {
      if (seq === dealSeq) error.value = err.message || 'Failed to fetch deal'
      throw err
    } finally {
      if (seq === dealSeq) loading.value = false
    }
  }

  const createDeal = async (dealData: Partial<Deal>) => {
    loading.value = true
    error.value = null
    try {
      const response = await apiClient.post('/deals', dealData)
      const rawDeal = response.data.data
      const newDeal = {
        ...rawDeal,
        id: rawDeal._id,
        createdAt: rawDeal.createdAt ? new Date(rawDeal.createdAt) : undefined,
        updatedAt: rawDeal.updatedAt ? new Date(rawDeal.updatedAt) : undefined,
      }
      deals.value.unshift(newDeal)
      return newDeal
    } catch (err: any) {
      error.value = err.message || 'Failed to create deal'
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateDeal = async (id: string, updates: Partial<Deal>) => {
    loading.value = true
    error.value = null
    try {
      const response = await apiClient.patch(`/deals/${id}`, updates)
      const rawDeal = response.data.data
      const updatedDeal = {
        ...rawDeal,
        id: rawDeal._id,
        createdAt: rawDeal.createdAt ? new Date(rawDeal.createdAt) : undefined,
        updatedAt: rawDeal.updatedAt ? new Date(rawDeal.updatedAt) : undefined,
      }

      // The PATCH answer carries none of the read-only extras (nextStep, stageEnteredAt, contactMissing,
      // leadQualification, plan): keep the ones on screen until the next read brings fresh ones,
      // or the Deal page flashes "No next step" between the save and its refetch.
      const index = deals.value.findIndex(l => l.id === id)
      if (index !== -1) {
        deals.value[index] = keepReadOnlyExtras(deals.value[index], updatedDeal)
      }

      if (currentDeal.value?.id === id) {
        currentDeal.value = keepReadOnlyExtras(currentDeal.value, updatedDeal)
      }

      return updatedDeal
    } catch (err: any) {
      error.value = err.message || 'Failed to update deal'
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteDeal = async (id: string) => {
    loading.value = true
    error.value = null
    try {
      await apiClient.delete(`/deals/${id}`)
      deals.value = deals.value.filter(l => l.id !== id)

      if (currentDeal.value?.id === id) {
        currentDeal.value = null
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to delete deal'
      throw err
    } finally {
      loading.value = false
    }
  }

  const convertToPipeline = async (id: string, pipelineData?: any) => {
    loading.value = true
    error.value = null
    try {
      const response = await apiClient.post(`/deals/${id}/topipeline`, pipelineData)
      const updatedDeal = response.data.data

      // Update deal in local state with the returned data
      const index = deals.value.findIndex(l => l.id === id)
      if (index !== -1) {
        deals.value[index] = {
          ...updatedDeal,
          id: updatedDeal._id,
          createdAt: updatedDeal.createdAt ? new Date(updatedDeal.createdAt) : undefined,
          updatedAt: updatedDeal.updatedAt ? new Date(updatedDeal.updatedAt) : undefined,
        }
      }

      return updatedDeal
    } catch (err: any) {
      error.value = err.message || 'Failed to add deal to pipeline'
      throw err
    } finally {
      loading.value = false
    }
  }

  const assignDeal = async (id: string, userId: string) => {
    return updateDeal(id, { assignedTo: userId })
  }

  const searchDeals = async (query: string) => {
    return fetchDeals({ q: query })
  }

  // Restore board-archived deals (NOT the `dealarchives` graveyard — see restoreDeal).
  // The deal reappears at the bottom of its stage column on the next board load, because
  // the backend's lazy position backfill recreates its deal_positions row there.
  const bulkUnarchive = async (dealIds: string[]) => {
    const notifications = useNotificationStore()
    loading.value = true
    error.value = null
    try {
      const result = await dealsApi.bulkUnarchive(dealIds)
      deals.value = deals.value.filter(d => !dealIds.includes(d._id || ''))
      const n = result?.updated ?? dealIds.length
      notifications.success(`Restored ${n} deal${n === 1 ? '' : 's'} from archive`)
      return result
    } catch (err: any) {
      error.value = err.message || 'Failed to restore deals'
      notifications.error(error.value || 'Failed to restore deals')
      throw err
    } finally {
      loading.value = false
    }
  }

  // Deleted-deal methods (the `dealarchives` graveyard, surfaced as "Deleted Deals")
  const fetchArchivedDeals = async (params?: { page?: number; pageSize?: number; search?: string }) => {
    loading.value = true
    error.value = null
    try {
      const response = await dealsApi.listArchived(params)
      archivedDeals.value = response.data || []
      archivedMeta.value = response.meta || null
      return archivedDeals.value
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch archived deals'
      throw err
    } finally {
      loading.value = false
    }
  }

  const restoreDeal = async (archiveId: string) => {
    const notifications = useNotificationStore()
    loading.value = true
    error.value = null
    try {
      await dealsApi.restoreArchived(archiveId)
      // Remove from archived list
      archivedDeals.value = archivedDeals.value.filter(d => d._id !== archiveId)
      if (archivedMeta.value) {
        archivedMeta.value.total -= 1
      }
      notifications.success('Deal restored successfully')
      return true
    } catch (err: any) {
      error.value = err.message || 'Failed to restore deal'
      notifications.error(error.value || 'Failed to restore deal')
      return false
    } finally {
      loading.value = false
    }
  }

  const permanentlyDeleteArchived = async (archiveId: string) => {
    const notifications = useNotificationStore()
    loading.value = true
    error.value = null
    try {
      await dealsApi.permanentlyDeleteArchived(archiveId)
      // Remove from archived list
      archivedDeals.value = archivedDeals.value.filter(d => d._id !== archiveId)
      if (archivedMeta.value) {
        archivedMeta.value.total -= 1
      }
      notifications.success('Deal permanently deleted')
      return true
    } catch (err: any) {
      error.value = err.message || 'Failed to delete deal'
      notifications.error(error.value || 'Failed to delete deal')
      return false
    } finally {
      loading.value = false
    }
  }

  const bulkUpdateOwner = async (
    dealIds: string[],
    newOwnerId: string,
  ): Promise<{ updated: number; failed: number } | null> => {
    const notifications = useNotificationStore()
    loading.value = true
    error.value = null
    try {
      const response = await dealsApi.bulkUpdateOwner(dealIds, newOwnerId)
      const result = response.data || response
      notifications.success(`Successfully updated ${result.updated} deal(s)`)
      if (result.failed > 0) {
        notifications.error(`${result.failed} deal(s) failed to update`)
      }
      return result
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to update deals'
      notifications.error(error.value || 'Failed to update deals')
      return null
    } finally {
      loading.value = false
    }
  }

  function getDealPriorityColor(priority: string): string {
    switch (priority) {
      case 'urgent':
        return 'text-danger-text bg-danger-tint'
      case 'high':
        return 'text-warning-text bg-warning-tint'
      case 'medium':
      case 'normal':
        return 'text-ink-2 bg-wash'
      case 'low':
        return 'text-ink-3 bg-wash'
      default:
        return 'text-ink-2 bg-wash'
    }
  }

  function getDealPriorityLabel(priority: string): string {
    return priority ? priority.charAt(0).toUpperCase() + priority.slice(1) : 'Normal'
  }

  const reset = () => {
    deals.value = []
    currentDeal.value = null
    error.value = null
    meta.value = null
    archivedDeals.value = []
    archivedMeta.value = null
  }

  return {
    // State
    deals,
    currentDeal,
    loading,
    error,
    meta,
    archivedDeals,
    archivedMeta,

    // Computed
    totalDeals,
    newDeals,
    qualifiedDeals,
    totalValue,
    staleDeals,
    dealsClosingThisWeek,
    openDeals,

    // Actions
    fetchDeals,
    bulkUnarchive,
    fetchDeal,
    createDeal,
    updateDeal,
    deleteDeal,
    convertToPipeline,
    assignDeal,
    searchDeals,
    fetchArchivedDeals,
    restoreDeal,
    permanentlyDeleteArchived,
    bulkUpdateOwner,
    getDealPriorityColor,
    getDealPriorityLabel,
    reset
  }
})
